import React from "react";
import { HintSurfaceContext } from "../../../components/shortcuts/Kbd";
import { CornerTag } from "./CornerTag";
import {
  planTitle,
  unlockedBenchPlans,
} from "../../../game/bench-work/plan-registry";
import { selectedBenchPlan } from "../../../game/bench-work/tool-work";
import { CONSUMABLE_TYPES } from "../../../game/Consumable";
import { formatCount } from "../../../utils/formatNumber";
import { clearMachineOperation } from "../../../sim/commands/machine-commands";
import { BenchAssemblyView } from "../../scenes/bench/BenchAssemblyView";
import { BenchDive } from "../../scenes/bench/BenchDive";
import { useGame, useShellVersion, useShopState } from "../../useShell";
import { BLUEPRINT_BLUE, BLUEPRINT_BLUE_DEEP } from "../station/BlueprintStack";
import { PlanBrowser } from "./PlanBrowser";
import { useBenchChrome } from "./useBenchDive";

/**
 * The bench's plans, as the thing they diegetically are: a pile of shop
 * drawings under the tool rail, grabbed like the tools above it — click
 * it to pull the plan folder up over the view (PlanBrowser, mounted by
 * BenchPlanBrowser below; the open state is BenchDive's, since the pile
 * and the folder mount apart). Drawings are filed there under category
 * tabs and pulled by name. Pulling a sheet IS selecting the plan: its
 * outlines land on the bench top, and the pulled drawing lies on top of
 * the pile, its title block reading the build's tally — pieces seated,
 * fasteners driven — since the drawing is what the tally belongs to. A
 * small ↩ tag on its corner (CornerTag) puts it back (the ghost outlines
 * leave the bench with it), and the pile's cover reads PLANS again.
 *
 * Tool work isn't a plan: a staged pallet offers its nails, a held tool
 * offers its strokes and cuts, and glue-ups are clamps-first. The drawer
 * holds only assembly builds.
 */
export const BenchPlanPile: React.FC = () => {
  const game = useGame();
  useShellVersion();
  const gameState = useShopState();
  const chrome = useBenchChrome();
  const dive = game.entities.tryGetSingleton(BenchDive);
  const bench = dive?.openBench();

  const plans = unlockedBenchPlans(gameState.progression);

  if (!dive || !bench || plans.length === 0) return null;

  const machine = bench.view();
  const selected = selectedBenchPlan(machine);
  // A running job resolves against the plan it finds when it finishes,
  // so the drawing holds still until the work comes off.
  const working = machine.state.operationProgress.status === "inProgress";
  const build =
    game.entities.tryGetSingleton(BenchAssemblyView)?.assemblyProgress() ??
    null;

  return (
    // Beside the rail's dark chrome, so the key cap takes the chrome face.
    <HintSurfaceContext.Provider value="chrome">
      <span
        className={`pointer-events-auto relative block ${chrome.className}`}
        inert={chrome.inert}
      >
        <button
          type="button"
          aria-expanded={dive.plansOpen}
          aria-label={
            selected
              ? `Plans — ${planTitle(selected)} is out`
              : "Open the plans"
          }
          title={selected ? planTitle(selected) : "Plans"}
          data-testid="blueprint-corner"
          data-sfx="ui-tab"
          onClick={(event) => {
            event.stopPropagation();
            dive.setPlansOpen(!dive.plansOpen);
          }}
          className="block cursor-pointer rounded p-1 transition-transform hover:-translate-y-0.5 hover:drop-shadow-[0_3px_4px_rgba(0,0,0,0.5)]"
        >
          <span className="relative block h-16 w-16 select-none">
            {/* The sheets under the top one, fanned a little */}
            {[
              { rotate: -6, dx: -2, dy: 1, deep: true },
              { rotate: 3, dx: 2, dy: 0, deep: false },
            ].map((sheet, index) => (
              <span
                key={index}
                aria-hidden
                className="absolute inset-0 rounded-[2px] border border-white/35 shadow"
                style={{
                  backgroundColor: sheet.deep
                    ? BLUEPRINT_BLUE_DEEP
                    : BLUEPRINT_BLUE,
                  transform: `translate(${sheet.dx}px, ${sheet.dy}px) rotate(${sheet.rotate}deg)`,
                }}
              >
                <span className="absolute inset-[3px] border border-white/25" />
              </span>
            ))}
            {/* The top sheet: the pile's cover, or the drawing that's out */}
            <span
              className="absolute inset-0 flex flex-col items-center justify-center rounded-[2px] border border-white/35 px-1.5 text-center shadow"
              style={{
                backgroundColor: BLUEPRINT_BLUE,
                transform: "translate(0px, -1px) rotate(-1deg)",
              }}
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-[3px] border border-white/25"
              />
              {selected ? (
                <>
                  <span className="line-clamp-3 font-condensed text-[0.5rem] font-bold uppercase leading-tight tracking-wide text-white">
                    {planTitle(selected)}
                  </span>
                  {build && (
                    <span
                      data-testid="bench-build-progress"
                      className="mt-0.5 font-condensed text-[0.45rem] leading-tight tabular-nums text-white/70"
                    >
                      {build.seated}/{build.slots} placed · {build.driven}/
                      {build.fasteners}{" "}
                      {build.fastenerId === "screws" ? "screwed" : "nailed"}
                    </span>
                  )}
                  {/* The fasteners still to drive read against the
                      cabinet right on the drawing: a bench at zero warms
                      up, since the Supplies tally hides when it's empty */}
                  {build && build.fasteners > 0 && (
                    <span
                      data-testid="bench-build-supply"
                      className={`font-condensed text-[0.45rem] leading-tight tabular-nums ${
                        build.onHand === 0 ? "text-amber-300" : "text-white/70"
                      }`}
                    >
                      {formatCount(build.onHand)}{" "}
                      {CONSUMABLE_TYPES[build.fastenerId].unit} on hand
                    </span>
                  )}
                </>
              ) : (
                <span className="font-stencil text-[0.7rem] font-bold uppercase tracking-[0.2em] text-white/90">
                  Plans
                </span>
              )}
            </span>
          </span>
        </button>
        {/* The pulled drawing goes back on the pile from its corner */}
        {selected && (
          <CornerTag
            testId="put-back-plan-chip"
            sfx="ui-page-turn"
            disabled={working}
            onClick={() => clearMachineOperation(game, bench)}
            label="Put the drawing back"
          >
            ↩
          </CornerTag>
        )}
      </span>
    </HintSurfaceContext.Provider>
  );
};

/**
 * The drawer the pile opens: the plans spread across the whole view.
 * Mounted at the HUD's top level, since the pile sits inside the rail's
 * column and a whole-view sheet can't.
 */
export const BenchPlanBrowser: React.FC = () => {
  const game = useGame();
  useShellVersion();
  const dive = game.entities.tryGetSingleton(BenchDive);
  const bench = dive?.openBench();
  if (!dive || !bench || !dive.plansOpen) return null;
  return (
    <PlanBrowser
      machine={bench.view()}
      bench={bench}
      onClose={() => dive.setPlansOpen(false)}
    />
  );
};
